import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface UserSettings {
  id: string;
  user_id: string;
  email: string;
  storage_limit_mb: number;
  storage_used_mb: number;
  ai_requests_limit: number;
  ai_requests_used: number;
  storage_notified_50: boolean;
  storage_notified_100: boolean;
  ai_notified_50: boolean;
  ai_notified_100: boolean;
  created_at: string;
  updated_at: string;
}

export function useUserSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    if (!user) {
      setSettings(null);
      setIsLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching user settings:', error);
    }

    if (data) {
      setSettings(data as UserSettings);
    } else if (user.email) {
      // Auto-create settings if they don't exist
      const { data: newSettings, error: insertError } = await supabase
        .from('user_settings')
        .insert({
          user_id: user.id,
          email: user.email,
          storage_limit_mb: 100,
          ai_requests_limit: 100,
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error creating user settings:', insertError);
      } else if (newSettings) {
        setSettings(newSettings as UserSettings);
      }
    }

    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const checkStorageLimit = useCallback(async (fileSizeMB: number): Promise<boolean> => {
    if (!settings || !user) return false;

    const newUsage = settings.storage_used_mb + fileSizeMB;

    if (newUsage > settings.storage_limit_mb) {
      toast.error(`Storage limit reached (${settings.storage_limit_mb}MB). Delete some files to upload more.`);
      return false;
    }

    return true;
  }, [settings, user]);

  const updateStorageUsage = useCallback(async (fileSizeMB: number): Promise<void> => {
    if (!settings || !user) return;

    const newUsage = settings.storage_used_mb + fileSizeMB;
    const usagePercent = (newUsage / settings.storage_limit_mb) * 100;

    const updates: Partial<UserSettings> = {
      storage_used_mb: newUsage,
    };

    // Check for 50% notification
    if (usagePercent >= 50 && !settings.storage_notified_50) {
      toast.info(`You've used 50% of your storage (${newUsage.toFixed(1)}MB / ${settings.storage_limit_mb}MB)`);
      updates.storage_notified_50 = true;
    }

    // Check for 100% notification (approaching limit)
    if (usagePercent >= 90 && !settings.storage_notified_100) {
      toast.warning(`You're almost out of storage (${usagePercent.toFixed(0)}% used)`);
      updates.storage_notified_100 = true;
    }

    await supabase
      .from('user_settings')
      .update(updates)
      .eq('user_id', user.id);

    setSettings(prev => prev ? { ...prev, ...updates } : null);
  }, [settings, user]);

  const reduceStorageUsage = useCallback(async (fileSizeMB: number): Promise<void> => {
    if (!settings || !user) return;

    const newUsage = Math.max(0, settings.storage_used_mb - fileSizeMB);

    await supabase
      .from('user_settings')
      .update({ storage_used_mb: newUsage })
      .eq('user_id', user.id);

    setSettings(prev => prev ? { ...prev, storage_used_mb: newUsage } : null);
  }, [settings, user]);

  const checkAILimit = useCallback(async (): Promise<boolean> => {
    if (!settings || !user) return false;

    if (settings.ai_requests_used >= settings.ai_requests_limit) {
      toast.error(`AI request limit reached (${settings.ai_requests_limit} requests). Contact support for more.`);
      return false;
    }

    return true;
  }, [settings, user]);

  const incrementAIUsage = useCallback(async (): Promise<void> => {
    if (!settings || !user) return;

    const newUsage = settings.ai_requests_used + 1;
    const usagePercent = (newUsage / settings.ai_requests_limit) * 100;

    const updates: Partial<UserSettings> = {
      ai_requests_used: newUsage,
    };

    // Check for 50% notification
    if (usagePercent >= 50 && !settings.ai_notified_50) {
      toast.info(`You've used 50% of your Partnr AI requests (${newUsage} / ${settings.ai_requests_limit})`);
      updates.ai_notified_50 = true;
    }

    // Check for approaching limit
    if (usagePercent >= 90 && !settings.ai_notified_100) {
      toast.warning(`You're almost out of AI requests (${usagePercent.toFixed(0)}% used)`);
      updates.ai_notified_100 = true;
    }

    await supabase
      .from('user_settings')
      .update(updates)
      .eq('user_id', user.id);

    setSettings(prev => prev ? { ...prev, ...updates } : null);
  }, [settings, user]);

  return {
    settings,
    isLoading,
    refetch: fetchSettings,
    checkStorageLimit,
    updateStorageUsage,
    reduceStorageUsage,
    checkAILimit,
    incrementAIUsage,
  };
}
